// Baselayer Background Checks — lien filing detail at
// /background-checks/:id/liens/:lienId. Reads the lien out of the Liens add-on's
// rawResponseJson (parsed client-side) and shows filing facts, debtors, secured
// parties, collateral, and an amendments table. "Download Lien Documents"
// streams the native filing PDF.

import { useParams, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import {
  Button,
  DetailLayout,
  DetailSection,
  PageHeader,
  Skeleton,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAddOns, useDownloadLienDocument } from "@/hooks/backgroundChecks/useAddOns";
import {
  findAddOn,
  formatLooseDate,
  DefinitionList,
} from "@/components/backgroundChecks/explorer/tabHelpers";
import {
  parseLiens,
  asString,
  pick,
  display,
  type ParsedLienParty,
} from "@/components/backgroundChecks/explorer/parse";

export default function LienDetailPage() {
  const { id, lienId } = useParams<{ id: string; lienId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const addOnsQuery = useAddOns(id);
  const download = useDownloadLienDocument(id ?? "");

  if (addOnsQuery.isLoading) {
    return (
      <div className="flex flex-col gap-space-4">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const addOn = findAddOn(addOnsQuery.data, "Liens");
  const liens = parseLiens(addOn?.rawResponseJson);
  const lien = liens.find((l) => l.id === lienId);

  if (!lien) {
    return (
      <DetailSection title="Lien not found">
        <p className="text-small text-gray-mid m-0">
          This lien filing could not be found on the current results.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate(`/background-checks/${id}`)}
        >
          Back to results
        </button>
      </DetailSection>
    );
  }

  const handleDownload = async () => {
    if (!id || !lienId) return;
    try {
      await download.mutateAsync(lienId);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not download the lien document.";
      toast({ message, tone: "error" });
    }
  };

  return (
    <DetailLayout
      header={
        <PageHeader
          breadcrumbs={[
            { label: "Background Checks", to: "/background-checks" },
            { label: "Results", to: `/background-checks/${id}` },
            { label: lien.filingNumber ?? "Lien" },
          ]}
          eyebrow="Lien filing"
          title={lien.filingType ?? lien.filingNumber ?? "Lien filing"}
          actions={
            <Button
              type="button"
              onClick={handleDownload}
              loading={download.isPending}
              disabled={download.isPending}
            >
              <Download size={16} aria-hidden /> Download Lien Documents
            </Button>
          }
        />
      }
      aside={
        <DetailSection title="Filing">
          <DefinitionList
            items={[
              { label: "Date filed", value: formatLooseDate(lien.filingDate) },
              { label: "Lapse date", value: formatLooseDate(lien.lapseDate) },
              { label: "State", value: lien.state ?? "—" },
              { label: "Status", value: lien.status ?? "—" },
              {
                label: "Lapsed",
                value: lien.lapsed == null ? "—" : lien.lapsed ? "Yes" : "No",
              },
              { label: "Filing number", value: lien.filingNumber ?? "—" },
            ]}
          />
        </DetailSection>
      }
    >
      <DetailSection title="Debtors">
        <PartyList parties={lien.debtors} />
      </DetailSection>

      <DetailSection title="Secured parties">
        <PartyList parties={lien.securedParties} />
      </DetailSection>

      <DetailSection title="Collateral statement">
        <p className="text-small text-onyx m-0 whitespace-pre-wrap">
          {lien.collateral ?? "—"}
        </p>
      </DetailSection>

      <DetailSection title="Amendments">
        {lien.amendments.length === 0 ? (
          <p className="text-small text-gray-mid m-0">No amendments.</p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Type</TH>
                <TH>Date</TH>
                <TH>Number</TH>
              </TR>
            </THead>
            <TBody>
              {lien.amendments.map((a, i) => (
                <TR key={i}>
                  <TD>{display(pick(a, "type", "amendment_type"))}</TD>
                  <TD>
                    {formatLooseDate(
                      asString(pick(a, "date", "filing_date")),
                    )}
                  </TD>
                  <TD>{display(pick(a, "number", "filing_number"))}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </DetailSection>
    </DetailLayout>
  );
}

function PartyList({ parties }: { parties: ParsedLienParty[] }) {
  if (parties.length === 0) {
    return <p className="text-small text-gray-mid m-0">—</p>;
  }
  return (
    <ul className="m-0 list-none flex flex-col gap-space-2">
      {parties.map((p, i) => (
        <li key={i} className="text-small text-onyx">
          <span className="font-medium">{p.name ?? "—"}</span>
          {p.address && (
            <span className="block text-gray-mid">{p.address}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
